import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { feature } from 'topojson-client';
import { AntiMetalButton } from './ui/anti-metal-button';
import './Footer.css';

// ── Random pin locations (lat, lng) ─────────────────────────────────────────
const PINS = [
  [40.7,  -74.0],   // New York
  [34.0, -118.2],   // Los Angeles
  [51.5,   -0.1],   // London
  [48.8,    2.3],   // Paris
  [25.2,   55.3],   // Dubai
  [1.35,  103.8],   // Singapore
  [35.7,  139.7],   // Tokyo
  [-33.9,  18.4],   // Cape Town
  [19.4,  -99.1],   // Mexico City
  [-23.5, -46.6],   // São Paulo
  [55.7,   37.6],   // Moscow
  [28.6,   77.2],   // New Delhi
  [-33.9, 151.2],   // Sydney
  [43.7,  -79.4],   // Toronto
  [37.6,  126.9],   // Seoul
];

// ── Globe ────────────────────────────────────────────────────────────────────
function Globe() {
  const canvasRef  = useRef(null);
  const rafRef     = useRef(null);
  const rotRef     = useRef(0);
  const geoRef     = useRef(null);
  const dragRef    = useRef({ active: false, lastX: 0 });
  const velRef     = useRef(0); // momentum

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const R   = 150;
    const W   = canvas.width;
    const H   = canvas.height;
    const cx  = W / 2;
    const cy  = H / 2;

    function project3D(lat, lng, rotDeg) {
      const phi   = (90 - lat)  * Math.PI / 180;
      const theta = (lng + rotDeg) * Math.PI / 180;
      const x = R * Math.sin(phi) * Math.cos(theta);
      const y = R * Math.cos(phi);
      const z = R * Math.sin(phi) * Math.sin(theta);
      return { x, y, z, px: cx + x, py: cy - y, visible: z >= -10 };
    }

    function drawRing(coords, rot) {
      if (!coords || coords.length < 2) return;
      ctx.beginPath();
      let started = false;
      let prevVisible = false;
      for (let i = 0; i < coords.length; i++) {
        const [lng, lat] = coords[i];
        const { px, py, visible } = project3D(lat, lng, rot);
        if (visible) {
          if (!started || !prevVisible) { ctx.moveTo(px, py); started = true; }
          else ctx.lineTo(px, py);
        }
        prevVisible = visible;
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth   = 0.6;
      ctx.stroke();
    }

    function draw(rot) {
      ctx.clearRect(0, 0, W, H);

      // Sphere
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fillStyle = '#000';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth   = 1;
      ctx.stroke();

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R - 0.5, 0, Math.PI * 2);
      ctx.clip();

      // Lat grid
      for (let lat = -60; lat <= 60; lat += 30) {
        const phi  = (90 - lat) * Math.PI / 180;
        const r2   = R * Math.sin(phi);
        const yOff = cy - R * Math.cos(phi);
        ctx.beginPath();
        ctx.ellipse(cx, yOff, r2, r2 * 0.13, 0, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.08)';
        ctx.lineWidth   = 0.5;
        ctx.stroke();
      }

      // Lng grid
      for (let lng = 0; lng < 360; lng += 30) {
        const a = (lng + rot) * Math.PI / 180;
        ctx.beginPath();
        let first = true;
        for (let lat = -90; lat <= 90; lat += 2) {
          const phi = (90 - lat) * Math.PI / 180;
          const x   = R * Math.sin(phi) * Math.cos(a);
          const z   = R * Math.sin(phi) * Math.sin(a);
          const y   = R * Math.cos(phi);
          if (z < 0) { first = true; continue; }
          const px = cx + x, py = cy - y;
          first ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
          first = false;
        }
        ctx.strokeStyle = 'rgba(255,255,255,0.08)';
        ctx.lineWidth   = 0.5;
        ctx.stroke();
      }

      // Country outlines
      if (geoRef.current) {
        geoRef.current.features.forEach(f => {
          const geom = f.geometry;
          if (!geom) return;
          if (geom.type === 'Polygon') geom.coordinates.forEach(ring => drawRing(ring, rot));
          else if (geom.type === 'MultiPolygon') geom.coordinates.forEach(poly => poly.forEach(ring => drawRing(ring, rot)));
        });
      }

      ctx.restore();

      // Pins
      PINS.forEach(([lat, lng]) => {
        const { px, py, visible } = project3D(lat, lng, rot);
        if (!visible) return;
        ctx.beginPath();
        ctx.arc(px, py, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = '#d4f240';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(px, py, 6.5, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(212,242,64,0.3)';
        ctx.lineWidth   = 1;
        ctx.stroke();
      });
    }

    function tick() {
      if (!dragRef.current.active) {
        // auto-rotate + momentum decay
        velRef.current *= 0.95;
        rotRef.current = (rotRef.current + 0.18 + velRef.current) % 360;
      }
      draw(rotRef.current);
      rafRef.current = requestAnimationFrame(tick);
    }

    fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json')
      .then(r => r.json())
      .then(world => {
        geoRef.current = feature(world, world.objects.countries);
        rafRef.current = requestAnimationFrame(tick);
      })
      .catch(() => { rafRef.current = requestAnimationFrame(tick); });

    // ── Mouse drag ──────────────────────────────────────────────────────────
    const onMouseDown = (e) => { dragRef.current = { active: true, lastX: e.clientX }; velRef.current = 0; };
    const onMouseMove = (e) => {
      if (!dragRef.current.active) return;
      const dx = e.clientX - dragRef.current.lastX;
      velRef.current = dx * 0.3;
      rotRef.current = (rotRef.current + dx * 0.3) % 360;
      dragRef.current.lastX = e.clientX;
    };
    const onMouseUp = () => { dragRef.current.active = false; };

    // ── Touch drag ──────────────────────────────────────────────────────────
    const onTouchStart = (e) => {
      dragRef.current = { active: true, lastX: e.touches[0].clientX };
      velRef.current = 0;
    };
    const onTouchMove = (e) => {
      if (!dragRef.current.active) return;
      const dx = e.touches[0].clientX - dragRef.current.lastX;
      velRef.current = dx * 0.3;
      rotRef.current = (rotRef.current + dx * 0.3) % 360;
      dragRef.current.lastX = e.touches[0].clientX;
    };
    const onTouchEnd = () => { dragRef.current.active = false; };

    canvas.addEventListener('mousedown',  onMouseDown);
    window.addEventListener('mousemove',  onMouseMove);
    window.addEventListener('mouseup',    onMouseUp);
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove',  onTouchMove,  { passive: true });
    canvas.addEventListener('touchend',   onTouchEnd);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      canvas.removeEventListener('mousedown',  onMouseDown);
      window.removeEventListener('mousemove',  onMouseMove);
      window.removeEventListener('mouseup',    onMouseUp);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove',  onTouchMove);
      canvas.removeEventListener('touchend',   onTouchEnd);
    };
  }, []);

  return <canvas ref={canvasRef} width={340} height={340} className="footer-globe" />;
}

// ── Analog clock (timezone-aware) ───────────────────────────────────────────
function getTimeInZone(timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  }).formatToParts(new Date());
  const pick = (type) => parseInt(parts.find((p) => p.type === type)?.value || '0', 10);
  const h24 = pick('hour');
  return { hours: h24 % 12, minutes: pick('minute'), seconds: pick('second') };
}

function AnalogClock({ timeZone }) {
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [timeZone]);

  const { hours, minutes } = getTimeInZone(timeZone);
  const hDeg = (hours + minutes / 60) * 30;
  const mDeg = minutes * 6;

  return (
    <svg className="footer-clock" viewBox="0 0 48 48" aria-hidden="true">
      <circle cx="24" cy="24" r="21" fill="none" stroke="currentColor" strokeWidth="1" />
      <line
        x1="24" y1="24" x2="24" y2="16"
        stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"
        transform={`rotate(${hDeg} 24 24)`}
      />
      <line
        x1="24" y1="24" x2="24" y2="11"
        stroke="currentColor" strokeWidth="0.9" strokeLinecap="round"
        transform={`rotate(${mDeg} 24 24)`}
      />
    </svg>
  );
}

// ── Collapsible mobile nav section ──────────────────────────────────────────
function NavSection({ heading, items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`footer-mob-section ${open ? 'footer-mob-section--open' : ''}`}>
      <button className="footer-mob-toggle" onClick={() => setOpen(o => !o)}>
        <span>{heading}</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <ul className="footer-mob-list">
        {items.map(item => (
          <li key={item}><a href="#">{item}</a></li>
        ))}
      </ul>
    </div>
  );
}

// ── Collapsible mobile location (clock visible when open) ───────────────────
function LocationMobSection({ heading, timeZone, items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`footer-mob-section ${open ? 'footer-mob-section--open' : ''}`}>
      <button type="button" className="footer-mob-toggle" onClick={() => setOpen((o) => !o)}>
        <span>{heading}</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className="footer-mob-list">
        <AnalogClock timeZone={timeZone} />
        <ul className="footer-mob-location-items">
          {items.map((item) => (
            <li key={item.key}>
              {item.href
                ? <a href={item.href}>{item.text}</a>
                : <span className="footer-addr-text">{item.text}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Data ─────────────────────────────────────────────────────────────────────
const LOCATIONS = [
  {
    label: 'India',
    timeZone: 'Asia/Kolkata',
    items: [
      { key: 'addr1', text: 'Plot No. 7-A, Industrial Estate,' },
      { key: 'addr2', text: 'Scheme No. 71, Indore, MP 452002' },
      { key: 'ph1', text: '+91 99817-18000', href: 'tel:+919981718000' },
      { key: 'ph2', text: '+91 94250-74535', href: 'tel:+919425074535' },
    ],
  },
  {
    label: 'United States',
    timeZone: 'America/Chicago',
    items: [
      { key: 'addr', text: 'Address to be added' },
      { key: 'ph1', text: '+1 (469) 207-3559', href: 'tel:+14692073559' },
    ],
  },
];

const MENU_LINKS = [
  { label: 'Services', to: '/services' },
  { label: 'About',    to: '/about'    },
  { label: 'Contact',  to: '/contact' },
];

// ── Footer ───────────────────────────────────────────────────────────────────
export default function Footer() {
  return (
    <footer className="footer">

      {/* ── TOP: Globe + CTA ── */}
      <div className="footer-body">
        <div className="footer-left">
          <p className="footer-label">ABOUT</p>
          <Globe />
        </div>
        <div className="footer-right">
          <div className="footer-tagline-block">
            <h2 className="footer-cta-heading">Ready to Grow?<br />Let's Talk.</h2>
            <p className="footer-cta-sub">
              Whether you're a D2C founder looking to scale, a brand stuck with a leaky funnel, or a business that needs to make better use of its data — we'd love to hear from you.
            </p>
            <AntiMetalButton as="a" href="mailto:hello@aurelixlabs.ai">Get in touch →</AntiMetalButton>

            {/* 4-col nav sits right below the button */}
            <div className="footer-nav">
              <div className="footer-nav-col">
                <p className="footer-label">Menu</p>
                <ul>
                  {MENU_LINKS.map(l => (
                    <li key={l.label}>
                      {l.to
                        ? <Link to={l.to} onClick={() => window.scrollTo(0,0)}>{l.label}</Link>
                        : <a href={l.href}>{l.label}</a>}
                    </li>
                  ))}
                </ul>
              </div>

              {LOCATIONS.map((loc) => (
                <div className="footer-nav-col footer-nav-col--location" key={loc.label}>
                  <div className="footer-col-heading">
                    <p className="footer-label">{loc.label}</p>
                    <AnalogClock timeZone={loc.timeZone} />
                  </div>
                  <ul>
                    {loc.items.map((item) => (
                      <li key={item.key}>
                        {item.href
                          ? <a href={item.href}>{item.text}</a>
                          : <span className="footer-addr-text">{item.text}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <div className="footer-nav-col">
                <p className="footer-label">Contact</p>
                <ul>
                  <li><a href="mailto:hello@aurelixlabs.ai">hello@aurelixlabs.ai</a></li>
                  <li><a href="tel:+919981718000">+91 99817-18000</a></li>
                  <li><a href="tel:+919425074535">+91 94250-74535</a></li>
                  <li><a href="tel:+14692073559">+1 (469) 207-3559</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE collapsible nav ── */}
      <div className="footer-mob-nav">
        <NavSection heading="Menu" items={['Services', 'About', 'Contact']} />
        {LOCATIONS.map((loc) => (
          <LocationMobSection key={loc.label} heading={loc.label} timeZone={loc.timeZone} items={loc.items} />
        ))}
        <NavSection heading="Contact" items={['hello@aurelixlabs.ai', '+91 99817-18000', '+91 94250-74535', '+1 (469) 207-3559']} />
      </div>
      <div className="footer-bottom">
        <Link to="/" className="footer-bottom-logo" onClick={() => window.scrollTo(0,0)}>
          <img src="/aurelix-logo.png" alt="Aurelix" className="footer-logo-img" />
        </Link>
        <span className="footer-copy">© {new Date().getFullYear()} Aurelix. All rights reserved.</span>
      </div>
{/* Footer video removed */}
    </footer>
  );
}
